import { useCallback } from 'react';
import { AxiosRequestConfig } from 'axios';
import {
  GetEmployeeAccountByIdResponse,
  InviteEmployeeRequestModel,
  GetEmployeeAccountsV2Response,
  InviteBulkEmployeeResponse,
  NoContentResult,
  DeleteBulkEmployeeAccountRequest,
  DeleteBulkEmployeeAccountResponse,
  GetByApplicationIdV2Response,
  GetAverageSecurityScoreV2Response,
  GetManagedByEmployeesByVaultIdV2Response,
  GetManagedByGroupsByVaultIdV2Response,
  Operation,
  ArchiveBulkEmployeeAccountRequest,
  ArchiveBulkEmployeeAccountResponse,
  SendBulkInviteResponse,
  RestoreBulkEmployeeAccountRequest,
  RestoreBulkEmployeeAccountResponse,
  MeResponse,
  EncryptionKeyType,
  SendBulkInviteRequest,
  RevokeAdminResponse,
  PromoteAdminResponse,
  GetUnprocessedSCIMUsersCountResponse,
  GetForLookupResponse,
} from '@uniqkey-backend-organization-web/api-client';
import { useApiClients } from '../../contexts/APIClientsContext';
import {
  TGetEmployeeAccountsByApplicationIdMethod,
  TGetEmployeeAccountsMethod,
  TGetEmployeeAccountsManagedByEmployeesByVaultIdMethod,
  TGetEmployeesManagedByGroupsByVaultIdMethod,
  TGetEmployeeAccountsForLookupMethod,
} from './interfaces';
import { dataExtractor } from '../../helpers/apiClients';
import {
  IInviteEmployeesParams,
} from '../../pages/EmployeesPage/components/InviteEmployeesModal/interfaces';

const useEmployeeAccountsAPI = () => {
  const { employeeAccountsAPIClient } = useApiClients();

  const inviteBulk = useCallback(async (
    params: IInviteEmployeesParams,
    options?: AxiosRequestConfig,
  ): Promise<InviteBulkEmployeeResponse> => {
    const { shouldInvite, language, users } = params;
    const employeeAccounts: InviteEmployeeRequestModel[] = users.map(({ email, name }) => ({
      email: email.toLowerCase(), name, language,
    }));
    return employeeAccountsAPIClient
      .apiV1EmployeeAccountsInviteBulkPost({ shouldInvite, employeeAccounts }, options)
      .then(dataExtractor<InviteBulkEmployeeResponse>());
  }, [employeeAccountsAPIClient]);

  const getEmployeeAccounts = useCallback<TGetEmployeeAccountsMethod>(
    (params, axiosRequestConfig) => {
      const {
        page,
        pageLength,
        typeFilter = undefined,
        groupId = undefined,
        statusFilter = undefined,
        searchQuery = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
        showProcessingUsers = undefined,
        showUnprocessedUsers = undefined,
        minSecurityScore = undefined,
        maxSecurityScore = undefined,
        isSecurityScoreEmpty = undefined,
        minUniqkeyAccessScore = undefined,
        maxUniqkeyAccessScore = undefined,
        isUniqkeyAccessScoreEmpty = false,
        activityFilter = undefined,
      } = params;
      return employeeAccountsAPIClient.apiV2EmployeeAccountsGet(
        page,
        pageLength,
        typeFilter,
        groupId,
        undefined, // 'vaultId' should not be used for now
        statusFilter,
        searchQuery,
        orderPropertyName,
        isDescending,
        false, // 'showSupporters' should not be used for now
        maxSecurityScore,
        minSecurityScore,
        maxUniqkeyAccessScore,
        minUniqkeyAccessScore,
        isSecurityScoreEmpty,
        isUniqkeyAccessScoreEmpty,
        activityFilter,
        showProcessingUsers,
        showUnprocessedUsers,
        axiosRequestConfig,
      ).then(dataExtractor<GetEmployeeAccountsV2Response>());
    },
    [employeeAccountsAPIClient],
  );

  const getEmployeeAccountsByApplicationId = useCallback<TGetEmployeeAccountsByApplicationIdMethod>(
    (params, axiosRequestConfig) => {
      const {
        page,
        pageLength,
        applicationId,
        searchQuery = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
      } = params;
      return employeeAccountsAPIClient.apiV2EmployeeAccountsGetByApplicationIdGet(
        page,
        pageLength,
        applicationId,
        searchQuery,
        orderPropertyName,
        isDescending,
        axiosRequestConfig,
      ).then(dataExtractor<GetByApplicationIdV2Response>());
    },
    [employeeAccountsAPIClient],
  );

  const getEmployeeAccountsManagedByEmployeesByVaultId = useCallback<
    TGetEmployeeAccountsManagedByEmployeesByVaultIdMethod
  >(
    (params, axiosRequestConfig) => {
      const {
        vaultId,
        page,
        pageLength,
        searchQuery = undefined,
        maxUniqkeyAccessScore = undefined,
        minUniqkeyAccessScore = undefined,
        isUniqkeyAccessScoreEmpty = false,
        orderPropertyName = undefined,
        isDescending = undefined,
      } = params;
      return employeeAccountsAPIClient
        .apiV2EmployeeAccountsGetManagedByEmployeesByVaultIdVaultIdGet(
          vaultId,
          page,
          pageLength,
          searchQuery,
          maxUniqkeyAccessScore,
          minUniqkeyAccessScore,
          isUniqkeyAccessScoreEmpty,
          orderPropertyName,
          isDescending,
          axiosRequestConfig,
        ).then(dataExtractor<GetManagedByEmployeesByVaultIdV2Response>());
    },
    [employeeAccountsAPIClient],
  );

  const getEmployeesManagedByGroupsByVaultId = useCallback<
    TGetEmployeesManagedByGroupsByVaultIdMethod
  >((params, axiosRequestConfig) => {
    const {
      vaultId,
      page,
      pageLength,
      searchQuery = undefined,
      maxUniqkeyAccessScore = undefined,
      minUniqkeyAccessScore = undefined,
      isUniqkeyAccessScoreEmpty = false,
      orderPropertyName = undefined,
      isDescending = undefined,
    } = params;
    return employeeAccountsAPIClient.apiV2EmployeeAccountsGetManagedByGroupsByVaultIdVaultIdGet(
      vaultId,
      page,
      pageLength,
      searchQuery,
      maxUniqkeyAccessScore,
      minUniqkeyAccessScore,
      isUniqkeyAccessScoreEmpty,
      orderPropertyName,
      isDescending,
      axiosRequestConfig,
    ).then(dataExtractor<GetManagedByGroupsByVaultIdV2Response>());
  }, [employeeAccountsAPIClient]);

  const getEmployeeAccountById = useCallback(
    (
      employeeAccountId: string,
      options?: AxiosRequestConfig,
    ): Promise<GetEmployeeAccountByIdResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsEmployeeAccountIdGet(employeeAccountId, options)
      .then(dataExtractor<GetEmployeeAccountByIdResponse>()),
    [employeeAccountsAPIClient],
  );

  const getCurrentEmployee = useCallback(
    (options?: AxiosRequestConfig)
      : Promise<MeResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsMeGet(options)
      .then(dataExtractor<MeResponse>()),
    [employeeAccountsAPIClient],
  );

  const patchEmployeeAccountById = useCallback(
    (employeeAccountId: string, operations: Array<Operation>, options?: AxiosRequestConfig)
      : Promise<NoContentResult> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsEmployeeAccountIdPatch(employeeAccountId, operations, options)
      .then(dataExtractor<NoContentResult>()),
    [employeeAccountsAPIClient],
  );

  const promoteAdmin = useCallback(
    (
      employeeAccountId: string,
      encryptionKeyType?: EncryptionKeyType,
      options?: AxiosRequestConfig,
    ): Promise<PromoteAdminResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsPromoteAdminPost(employeeAccountId, encryptionKeyType, options)
      .then(dataExtractor<PromoteAdminResponse>()),
    [employeeAccountsAPIClient],
  );

  const revokeAdmin = useCallback(
    (
      employeeAccountId: string,
      encryptionKeyType?: EncryptionKeyType,
      options?: AxiosRequestConfig,
    ): Promise<RevokeAdminResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsRevokeAdminPost(employeeAccountId, encryptionKeyType, options)
      .then(dataExtractor<RevokeAdminResponse>()),
    [employeeAccountsAPIClient],
  );

  const archiveEmployeeAccounts = useCallback(
    (
      archiveBulkEmployeeAccountRequest: ArchiveBulkEmployeeAccountRequest,
      options?: AxiosRequestConfig,
    ): Promise<ArchiveBulkEmployeeAccountResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsArchiveBulkPost(archiveBulkEmployeeAccountRequest, options)
      .then(dataExtractor<ArchiveBulkEmployeeAccountResponse>()),
    [employeeAccountsAPIClient],
  );

  const deleteEmployeeAccounts = useCallback(
    (
      deleteBulkEmployeeAccountRequest: DeleteBulkEmployeeAccountRequest,
      options?: AxiosRequestConfig,
    ): Promise<DeleteBulkEmployeeAccountResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsDeleteBulkPost(deleteBulkEmployeeAccountRequest, options)
      .then(dataExtractor<DeleteBulkEmployeeAccountResponse>()),
    [employeeAccountsAPIClient],
  );

  const getAverageSecurityScore = useCallback(
    (employeeAccountId: string, options?: AxiosRequestConfig)
      : Promise<GetAverageSecurityScoreV2Response> => employeeAccountsAPIClient
      .apiV2EmployeeAccountsGetAverageSecurityScoreEmployeeAccountIdGet(employeeAccountId, options)
      .then(dataExtractor<GetAverageSecurityScoreV2Response>()),
    [employeeAccountsAPIClient],
  );

  const sendInviteBulk = useCallback(
    (sendBulkInviteRequest: SendBulkInviteRequest, options?: AxiosRequestConfig)
      : Promise<SendBulkInviteResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsBulkSendInvitesPost(sendBulkInviteRequest, options)
      .then(dataExtractor<SendBulkInviteResponse>()),
    [employeeAccountsAPIClient],
  );

  const restoreEmployeeAccounts = useCallback(
    (
      restoreBulkEmployeeAccountRequest: RestoreBulkEmployeeAccountRequest,
      options?: AxiosRequestConfig,
    ): Promise<RestoreBulkEmployeeAccountResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsRestoreBulkPost(restoreBulkEmployeeAccountRequest, options)
      .then(dataExtractor<RestoreBulkEmployeeAccountResponse>()),
    [employeeAccountsAPIClient],
  );

  const getUnprocessedSCIMUsersCount = useCallback(
    (
      options?: AxiosRequestConfig,
    ): Promise<GetUnprocessedSCIMUsersCountResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsGetUnprocessedSCIMUsersCountGet(options)
      .then(dataExtractor<GetUnprocessedSCIMUsersCountResponse>()),
    [employeeAccountsAPIClient],
  );

  const getEmployeeAccountsForLookup = useCallback<TGetEmployeeAccountsForLookupMethod>(
    (params, axiosRequestConfig) => {
      const {
        page,
        pageLength,
        searchQuery = undefined,
        showSupporters = undefined,
        groupId = undefined,
        onlyValidStatusForGroupMembers = undefined,
      } = params;
      return employeeAccountsAPIClient
        .apiV1EmployeeAccountsGetForLookupGet(
          page,
          pageLength,
          searchQuery,
          showSupporters,
          groupId,
          onlyValidStatusForGroupMembers,
          axiosRequestConfig,
        )
        .then(dataExtractor<GetForLookupResponse>());
    },
    [employeeAccountsAPIClient],
  );

  return {
    inviteBulk,
    getCurrentEmployee,
    getEmployeeAccounts,
    getEmployeeAccountsByApplicationId,
    getEmployeeAccountsManagedByEmployeesByVaultId,
    getEmployeesManagedByGroupsByVaultId,
    getEmployeeAccountById,
    patchEmployeeAccountById,
    promoteAdmin,
    revokeAdmin,
    archiveEmployeeAccounts,
    deleteEmployeeAccounts,
    getAverageSecurityScore,
    sendInviteBulk,
    restoreEmployeeAccounts,
    getUnprocessedSCIMUsersCount,
    getEmployeeAccountsForLookup,
  };
};

export default useEmployeeAccountsAPI;
